import { Add } from "@mui/icons-material";
import { Box, Button, Typography, useMediaQuery, useTheme } from "@mui/material";
import { AxiosError } from "axios";
import { FC, ReactNode, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CustomDialog } from "../../common/components/CustomDialog";
import CustomLoader from "../../common/components/CustomLoader";
import CustomSnackbar from "../../common/components/CustomSnackbar";
import CustomTable from "../../common/components/CustomTable";
import { getForm } from "../../common/config/forms";
import { areaExpertiseHeadcells } from "../../common/config/headcells";
import { useAuth } from "../../common/hooks/useAuth";
import { useLoading } from "../../common/hooks/useLoading";
import { IAreaExpertise, IAreaExpertiseBody } from "../../common/interfaces/areaExpertise";
import { ICustomDialogProps } from "../../common/interfaces/dialog";
import { ICustomOption } from "../../common/interfaces/form";
import { ICustomPaginationProps } from "../../common/interfaces/pagination";
import { ACTIONS } from "../../common/interfaces/table";
import areaExpertiseService from "../../common/services/areaExpertiseService";
import userService from "../../common/services/userService";

interface IAreasExpertiseProps { }

const AreasExpertise: FC<IAreasExpertiseProps> = () => {

    // global
    const { permissionsState } = useAuth();
    const { loading } = useLoading();
    const { t } = useTranslation();
    const theme = useTheme();
    const largeMediumScreen = useMediaQuery(theme.breakpoints.up("sm"));

    const [action, setAction] = useState<ACTIONS>();
    const [areas, setAreas] = useState<(IAreaExpertise & { managerFullname: string, actions: { type: ACTIONS, message: string }[] })[]>([]);
    const [currentArea, setCurrentArea] = useState<IAreaExpertise | undefined>(undefined);

    // form
    const addAreaForm = getForm<IAreaExpertiseBody>("addArea");
    const [usersOptions, setUsersOptions] = useState<ICustomOption[]>([]);

    // pagination
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // dialogs
    const [openDialog, setOpenDialog] = useState(false);
    const [dialogTitle, setDialogTitle] = useState("");
    const [dialogBody, setDialogBody] = useState<ReactNode>(<></>);

    // snackbar
    const [openSnackbar, setOpenSnackbar] = useState<boolean>(false);
    const [messageSnackbar, setMessageSnackbar] = useState<string>("");
    const [responseType, setResponseType] = useState<"success" | "error">("success");

    const setActionsWithPermissions = () => {
        let tempActions: ({ type: ACTIONS, message: string })[] = []
        if (permissionsState && permissionsState.existEditAreasPermission)
            tempActions.push({
                type: ACTIONS.EDIT,
                message: t("areasExpertisePage.editAreaButtonLabel")
            })
        if (permissionsState && permissionsState.existDeleteAreasPermission)
            tempActions.push({
                type: ACTIONS.DELETE,
                message: t("areasExpertisePage.deleteAreaButtonLabel")
            })
        tempActions = tempActions.filter(action => action !== undefined)
        return tempActions
    }

    const getAreasList = useCallback(async () => {
        try {
            const response = await areaExpertiseService.getAll(page * rowsPerPage, rowsPerPage);
            let rows = response.content.map(area => {
                return {
                    ...area,
                    managerFullname: area.manager ? area.manager.firstName + " " + area.manager.lastName : "-",
                    actions: setActionsWithPermissions()
                }
            })
            setAreas(rows)
        }
        catch (error) {
            setOpenSnackbar(true);
            setResponseType("error");
            if (error instanceof AxiosError) {
                setMessageSnackbar(error.response?.data.title)
            } else {
                setMessageSnackbar(t("genericError"));
            }
        }
    }, [page, rowsPerPage])

    const getUsersOptionsList = async () => {
        try {
            const response = await userService.getAll(0, 1000);
            let options: ICustomOption[] = response.content.map(user => {
                return {
                    desc: user.firstName + " " + user.lastName + " - " + user.emailAddress,
                    value: user.id
                }
            })
            setUsersOptions(options);
        }
        catch (error) {
            setOpenSnackbar(true);
            setResponseType("error");
            if (error instanceof AxiosError) {
                setMessageSnackbar(error.response?.data.title)
            } else {
                setMessageSnackbar(t("genericError"));
            }
        }
    }

    useEffect(() => {
        getAreasList();
    }, [getAreasList, page, rowsPerPage])

    // page change handler (pagination)
    const handleChangePage = (
        _event: React.MouseEvent<HTMLButtonElement> | null,
        newPage: number
    ) => {
        setPage(newPage);
    };

    // num. of rows change handler (p                                                                                                                            agination)
    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        setRowsPerPage(parseInt(event.target.value));
    };

    // action handler (icon buttons from the table)
    const onActionClicked = (label: ACTIONS, data: IAreaExpertise & { actions: { type: ACTIONS, message: string }[] } | undefined) => {
        setAction(label);
        if (data) {
            const { actions, ...areaData } = data
            setCurrentArea(areaData);
        }
        switch (label) {
            case "add": {
                getUsersOptionsList();
                setDialogTitle(t("areasExpertisePage.addDialogTitle"));
                setDialogBody(undefined);
                setOpenDialog(true);
                break;
            }
            case "edit": {
                if (data) {
                    getUsersOptionsList();
                    setDialogTitle(t("areasExpertisePage.editDialogTitle") + '"' + data.name + '"');
                    setDialogBody(undefined);
                    setOpenDialog(true);
                }
                break;
            }
            case "delete": {
                if (data) {
                    setDialogTitle(t("areasExpertisePage.deleteDialogTitle") + '"' + data.name + '"?');
                    setDialogBody(
                        <Typography variant="body1">
                            {t("areasExpertisePage.deleteDialogWarningText")}
                        </Typography>
                    );
                    setOpenDialog(true);
                }
                break;
            }
            default: {
                break;
            }
        }
    };

    // close dialog handler
    const handleClose = () => {
        setOpenDialog(false);
    };

    // api request handler
    const apiRequest = (data: IAreaExpertiseBody | string | undefined) => {
        if (data && typeof data !== "string") {
            if (action === "add" && data)
                return areaExpertiseService.post(data)
            if (action === "edit" && currentArea)
                return areaExpertiseService.put(currentArea.id, data)
        } else if (action === "delete" && currentArea) {
            return areaExpertiseService.delete(currentArea.id)
        } else {
            return Promise.reject()
        }
    }

    // confirm dialog handler
    const handleActionConfirm = async (data: IAreaExpertiseBody | undefined) => {
        handleClose();
        try {
            await apiRequest(data);
            setOpenSnackbar(true);
            setResponseType("success");
            setMessageSnackbar(t("genericSuccess"));
            await getAreasList();
        } catch (error: unknown) {
            setOpenSnackbar(true);
            setResponseType("error");
            if (error instanceof AxiosError) {
                setMessageSnackbar(error.response?.data.content.message)
            } else {
                setMessageSnackbar(t("genericError"));
            }
        }
    }

    // pagination props
    const paginationProps: ICustomPaginationProps = {
        type: "table",
        paginationProps: {
            count: areas.length,
            page,
            onPageChange: handleChangePage,
            rowsPerPage,
            onRowsPerPageChange: handleChangeRowsPerPage,
        },
    };

    // dialog props
    const dialogProps: ICustomDialogProps = {
        type: action === "delete" ? "confirm" : "form",
        close: handleClose,
        confirm: () => handleActionConfirm(undefined),
        title: dialogTitle,
        open: openDialog,
        confirmButtonLabel: action === "delete" ? t("areasExpertisePage.areaForm.confirmButton") : t("areasExpertisePage.areaForm.saveChangesButton"),
        closeButtonLabel: t("areasExpertisePage.areaForm.cancelButton"),
        fullWidth: true,
        formProps: {
            ...addAreaForm,
            inputs: [
                ...addAreaForm.inputs.map(input => {
                    if (input.name === "managerId")
                        return { ...input, options: usersOptions }
                    else
                        return input
                })
            ],
            initialValues: action === "add" ? {
                name: "",
                managerId: ""
            } : { ...currentArea },
            isDialog: true,
            submit: (data) => handleActionConfirm(data),
            customStyles: {
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
            }
        }
    };

    return <Box sx={{ py: 1, px: 2, minHeight: "calc(100vh - 198px)" }}>
        <CustomDialog {...dialogProps}>{dialogBody}</CustomDialog>
        <CustomSnackbar
            type={responseType}
            message={messageSnackbar}
            open={openSnackbar}
            onClose={() => setOpenSnackbar(false)}
        />
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="h6" sx={{ mb: 0 }}>
                {t("areasExpertisePage.title")}
            </Typography>
            {
                permissionsState &&
                permissionsState.existCreateAreasPermission &&
                <Button
                    sx={(theme) => ({
                        [theme.breakpoints.only("xs")]: {
                            minWidth: { xs: 0 },
                            width: { xs: 34 },
                            height: { xs: 32 },
                            padding: { xs: 2 }
                        }
                    })}
                    variant="contained"
                    onClick={() => onActionClicked(ACTIONS.ADD, undefined)}
                >
                    <Add sx={{ mr: largeMediumScreen ? 0.5 : 0 }} />
                    {largeMediumScreen && t("areasExpertisePage.newAreaButton")}
                </Button>
            }
        </Box>
        {
            loading && !openDialog ? <CustomLoader /> : <Box>
                <CustomTable
                    headcells={
                        permissionsState &&
                            permissionsState.existAreasPermissions ?
                            areaExpertiseHeadcells :
                            [...areaExpertiseHeadcells.filter(headcell => headcell.label !== "action")]
                    }
                    rows={areas}
                    onIconActionClicked={onActionClicked}
                    pagination={paginationProps}
                />
            </Box>
        }
    </Box>
}

export default AreasExpertise;